import { Fragment } from 'react';
import { PiArrowSquareOutBold } from 'react-icons/pi';
import { Badge, LoadingState } from '@v2/design-system';
import { useI18n } from '@v2/providers/I18nProvider';
import type { PloomesDealDetail, PloomesInteraction } from '../types';
import { formatBrl, formatDate, formatDateTime } from '../utils';
import { useDealRelations } from './useDealRelations';

const MENTION_RE = /(?<![A-Za-zÀ-ÿ0-9._-])@([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ0-9_]*)/g;

function avatarFor(interaction: PloomesInteraction): { initial: string; hue: number } {
    const seed =
        interaction.creator_name ||
        interaction.content?.match(MENTION_RE)?.[0]?.replace('@', '').replaceAll('_', ' ') ||
        'Ploomes';
    const initial = (seed.trim().charAt(0) || 'P').toUpperCase();
    let hash = 0;
    for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    return { initial, hue: hash % 360 };
}

function relativeTime(iso: string | null | undefined): string {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const day = 86400000;
    if (diff < 60_000) return 'agora';
    if (diff < 3_600_000) return `há ${Math.round(diff / 60_000)} min`;
    if (diff < day) return `há ${Math.round(diff / 3_600_000)} h`;
    if (diff < 30 * day) return `há ${Math.round(diff / day)} dias`;
    if (diff < 365 * day) return `há ${Math.round(diff / (30 * day))} meses`;
    return `há ${Math.round(diff / (365 * day))} anos`;
}

function renderContent(text: string | null | undefined) {
    if (!text) return null;
    const out: React.ReactNode[] = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    MENTION_RE.lastIndex = 0;
    while ((m = MENTION_RE.exec(text)) !== null) {
        if (m.index > lastIndex) out.push(text.slice(lastIndex, m.index));
        const label = m[1].replaceAll('_', ' ');
        out.push(
            <span
                key={`m-${m.index}`}
                className="rounded bg-orange-50 px-1 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-950/50 dark:text-orange-300"
            >
                @{label}
            </span>,
        );
        lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) out.push(text.slice(lastIndex));
    return out.map((node, idx) => <Fragment key={idx}>{node}</Fragment>);
}

function RelationsEmpty({ title, hint }: { title: string; hint: string }) {
    return (
        <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center dark:border-zinc-700">
            <p className="text-sm font-medium text-gray-700 dark:text-zinc-200">{title}</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">{hint}</p>
        </div>
    );
}

export function DealTimelinePanel({ deal }: { deal: PloomesDealDetail }) {
    const { t } = useI18n();
    const { data, isLoading, isError } = useDealRelations(deal.id);
    const interactions = data?.ploomes_interactions ?? deal.ploomes_interactions ?? [];

    if (isLoading && !data && !(deal.ploomes_interactions?.length)) {
        return <LoadingState variant="detail" />;
    }

    if (isError && interactions.length === 0) {
        return (
            <p className="py-6 text-sm text-gray-500 dark:text-zinc-400">
                {t('v2.ploomes_deals.relations_load_error')}
            </p>
        );
    }

    if (interactions.length === 0) {
        return (
            <RelationsEmpty
                title={t('v2.ploomes_deals.timeline_empty')}
                hint={t('v2.ploomes_deals.timeline_empty_hint')}
            />
        );
    }

    return (
        <ol className="relative space-y-5 ps-1">
            {interactions.map((i, idx, arr) => {
                const { initial, hue } = avatarFor(i);
                const isLast = idx === arr.length - 1;
                return (
                    <li key={i.id} className="relative flex gap-3">
                        {!isLast && (
                            <span
                                aria-hidden
                                className="absolute start-[1.05rem] top-9 h-[calc(100%+0.25rem)] w-px bg-gray-200 dark:bg-zinc-700"
                            />
                        )}
                        <span
                            className="relative z-10 mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white shadow-sm ring-2 ring-white dark:ring-zinc-900"
                            style={{ backgroundColor: `hsl(${hue} 55% 50%)` }}
                        >
                            {initial}
                        </span>
                        <div className="min-w-0 flex-1 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 dark:border-zinc-700 dark:bg-zinc-800/60">
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                <span className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                                    {i.creator_name || 'Ploomes'}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-zinc-400">{formatDateTime(i.date)}</span>
                                <span className="text-xs text-gray-400 dark:text-zinc-500">· {relativeTime(i.date)}</span>
                            </div>
                            {i.title && (
                                <p className="mt-1 text-sm font-medium text-gray-800 dark:text-zinc-200">{i.title}</p>
                            )}
                            {i.content && (
                                <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700 dark:text-zinc-300">
                                    {renderContent(i.content)}
                                </p>
                            )}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

export function DealTasksPanel({ deal }: { deal: PloomesDealDetail }) {
    const { t } = useI18n();
    const { data, isLoading, isError } = useDealRelations(deal.id);
    const tasks = data?.ploomes_tasks ?? deal.ploomes_tasks ?? [];

    if (isLoading && !data && !(deal.ploomes_tasks?.length)) {
        return <LoadingState variant="table" />;
    }

    if (isError && tasks.length === 0) {
        return (
            <p className="py-6 text-sm text-gray-500 dark:text-zinc-400">
                {t('v2.ploomes_deals.relations_load_error')}
            </p>
        );
    }

    if (tasks.length === 0) {
        return (
            <RelationsEmpty
                title={t('v2.ploomes_deals.tasks_empty')}
                hint={t('v2.ploomes_deals.tasks_empty_hint')}
            />
        );
    }

    return (
        <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {tasks.map((task) => (
                <li key={task.id} className="flex items-center justify-between py-2">
                    <div>
                        <p className="font-medium text-gray-900 dark:text-zinc-100">{task.title}</p>
                        <p className="text-xs text-gray-500 dark:text-zinc-400">{formatDateTime(task.date_time)}</p>
                    </div>
                    <Badge color={task.finished ? 'success' : 'warning'}>
                        {task.finished ? 'Concluída' : 'Aberta'}
                    </Badge>
                </li>
            ))}
        </ul>
    );
}

export function DealOrdersPanel({ deal }: { deal: PloomesDealDetail }) {
    const { t } = useI18n();
    const { data, isLoading } = useDealRelations(deal.id);
    const orders = data?.ploomes_orders ?? deal.ploomes_orders ?? [];

    if (isLoading && !data && !(deal.ploomes_orders?.length)) {
        return <LoadingState variant="table" />;
    }

    if (orders.length === 0) {
        return <p className="py-6 text-sm text-gray-500 dark:text-zinc-400">Nenhum pedido vinculado.</p>;
    }

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead className="text-xs uppercase text-gray-500 dark:text-zinc-400">
                    <tr>
                        <th className="py-2 text-left">Pedido</th>
                        <th className="py-2 text-left">Data</th>
                        <th className="py-2 text-left">Qtd itens</th>
                        <th className="py-2 text-left">Valor</th>
                        <th className="py-2 text-left">Na proposta</th>
                        <th className="py-2 text-left">PDF</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                    {orders.map((o) => (
                        <tr key={o.id}>
                            <td className="py-2 font-medium text-gray-900 dark:text-zinc-100">
                                {o.order_number || '—'}
                            </td>
                            <td className="py-2 text-gray-700 dark:text-zinc-300">{formatDate(o.date)}</td>
                            <td className="py-2 text-gray-700 dark:text-zinc-300">{o.products_quantity_total}</td>
                            <td className="py-2 text-gray-700 dark:text-zinc-300">{formatBrl(o.amount)}</td>
                            <td className="py-2">
                                <Badge color={o.is_used_in_proposal ? 'success' : 'secondary'}>
                                    {o.is_used_in_proposal ? 'Sim' : 'Não'}
                                </Badge>
                            </td>
                            <td className="py-2">
                                {o.document_url ? (
                                    <a
                                        href={o.document_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                                    >
                                        {t('v2.ploomes_deals.relations_open')}
                                        <PiArrowSquareOutBold className="size-3.5" />
                                    </a>
                                ) : (
                                    <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function DealAttachmentsPanel({ deal }: { deal: PloomesDealDetail }) {
    const { t } = useI18n();
    const { data, isLoading } = useDealRelations(deal.id);
    const attachments = data?.ploomes_attachments ?? deal.ploomes_attachments ?? [];

    if (isLoading && !data && !(deal.ploomes_attachments?.length)) {
        return <LoadingState variant="table" />;
    }

    if (attachments.length === 0) {
        return (
            <p className="py-6 text-sm text-gray-500 dark:text-zinc-400">
                {t('v2.ploomes_deals.attachments_empty')}
            </p>
        );
    }

    return (
        <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {attachments.map((a) => {
                const href = a.access_url || a.url;
                return (
                    <li key={a.id} className="flex items-center justify-between gap-3 py-2">
                        <div className="min-w-0">
                            <p className="truncate font-medium text-gray-900 dark:text-zinc-100">{a.name}</p>
                            <p className="text-xs text-gray-500 dark:text-zinc-400">
                                {a.category || '—'} · {formatDateTime(a.ploomes_created_at)}
                                {a.is_pdf ? ' · PDF' : ''}
                            </p>
                        </div>
                        {href && (
                            <a
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                className="shrink-0 text-sm font-medium text-primary hover:underline"
                            >
                                {t('v2.ploomes_deals.relations_open')}
                            </a>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
